import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check, CalendarDays, MapPin, Percent, Phone, ShieldCheck, Layers, PiggyBank } from "lucide-react";
import { post } from "../lib/api";
import { leadSourceFields } from "../lib/leadSource";
import { measureLead } from "../lib/openaiPixel";
import { Eyebrow, Reveal, MaskLineView, Faq } from "../components/ui-bits";
import BookMeeting from "../components/BookMeeting";
import JsonLd from "../components/JsonLd";
import { useLang } from "../i18n/LangContext";
import { PHONE_DISPLAY, PHONE_TEL } from "../components/Navbar";

const HERO_IMG = "/investors-hero.jpg";

const field =
  "w-full rounded-lg border border-sand bg-white px-4 py-3 text-sm outline-none transition-colors placeholder:text-fog/60 focus:border-verde-700";
const label = "mb-1.5 block text-xs font-medium text-ink";

const COPY = {
  en: {
    eyebrow: "Investor information",
    h1a: "Request investor",
    h1b: "information.",
    heroCopy: "Invest in short-term, senior-secured loans to experienced real estate developers, financing land acquisition, licensing and pre-construction projects in Portugal.",
    proof: ["8% target annual yield, paid quarterly", "First-ranking mortgage over each asset", "Verde invests its own capital in every facility"],
    formTitle: "I want to Invest",
    done: "Request received.",
    doneCopy: "Verde Capital will be in touch shortly. A confirmation has been sent to ",
    name: "Name *", email: "Email *", country: "Country *", itype: "Investor type *",
    itypePh: "Select", ticket: "Indicative ticket *", ticketPh: "Select",
    message: "Message",
    consent: "I consent to Verde Capital contacting me about investor opportunities.",
    err: "Something went wrong. Please try again.",
    sending: "Sending…", submit: "Submit my application",
    investorTypes: ["Family office", "Private investor", "Entrepreneur investing through a company", "Adviser", "Other"],
    tickets: ["€100–250k", "€250–500k", "€500k–1m", "€1m+", "not yet determined"],
    secEyebrow: "What you get",
    secH: "The facts, before you ask.",
    securities: [
      { icon: Percent, t: "8% target annual interest", d: "Paid quarterly, directly to your designated bank account, for the life of the loan." },
      { icon: CalendarDays, t: "6 – 24 month terms", d: "Short terms with a defined repayment route: bank refinancing, sale or pre-sales." },
      { icon: ShieldCheck, t: "First-ranking mortgage", d: "Your capital is secured by a first-ranking mortgage over the underlying property." },
      { icon: PiggyBank, t: "Verde invests alongside", d: "Verde Capital invests its own capital in every facility, alongside yours." },
      { icon: Layers, t: "Disciplined underwriting", d: "Independent valuations, local market expertise and clear exit routes on every case." },
      { icon: MapPin, t: "Portuguese residential focus", d: "Loans on residential projects across Portugal — €100,000 minimum ticket." },
    ],
    oppEyebrow: "The opportunity",
    oppH1: "Portugal needs more homes.", oppH2: "Developers need capital.",
    oppCopy: "Portugal has a significant housing shortage. Strong demand, limited new supply and planning complexity prevent many viable projects from reaching construction. We provide the capital for the critical early stages where experienced developers need funding before banks step in.",
    oppNumber: "~300,000", oppNumberLabel: "homes missing in Portugal",
    oppCard: "Estimated housing construction shortfall relative to household growth over the past decade.",
    oppSource: "Source: Banco de Portugal, 2026 reporting on the housing construction gap.",
    faqEyebrow: "Frequently asked questions", faqH: "Key questions, clear answers.",
    faqs: [
      { q: "How does the 8% yield get paid?", a: "Quarterly, directly to your designated bank account, for the life of the loan." },
      { q: "When do I get my capital back?", a: "At the end of the loan term, repaid through the agreed route: bank refinancing, sale or pre-sales." },
      { q: "What is my capital secured by?", a: "A first-ranking mortgage over the underlying property — and Verde Capital invests its own capital alongside yours in every facility." },
      { q: "What is a first-ranking mortgage?", a: "It means the lender's charge over the property ranks ahead of any other lender's. If a loan ever needs to be recovered through the asset, a first-ranking position is repaid before subordinate creditors. Verde Capital facilities are secured this way." },
      { q: "What are the typical loan terms?", a: "6 – 24 months, with a defined repayment route: bank refinancing, sale or pre-sales." },
      { q: "Who can invest?", a: "Professional and qualified investors — private individuals, family offices and companies — from a €100,000 ticket, subject to onboarding and suitability checks." },
      { q: "What fees apply?", a: "Fees are set out in each facility's terms before you commit. There is no charge to request information or speak with the team." },
      { q: "What are the main risks?", a: "Development lending carries real risks: delays, licensing, market movements. Capital is at risk, and past performance is not a guide to future returns. Our mitigants are senior-secured positions, first-ranking mortgages, low loan-to-value lending and short terms." },
    ],
    contactEyebrow: "Investor conversation",
    contactH1: "Let's discuss", contactH2: "your investment.",
    contactCopy: "Schedule a call with our investor relations team to discuss the strategy, current pipeline and terms.",
    team: "Verde Capital investor relations", line: "One direct line. No call centres.",
    book: "Book a meeting", callPre: "Or call: ",
    alt: "Coastal Portuguese development site at dusk",
    disclaimer: "Capital is at risk, and past performance is not a guide to future returns. All lending is subject to underwriting, security and full due diligence.",
  },
  pt: {
    eyebrow: "Informação para investidores",
    h1a: "Pedir informação",
    h1b: "para investidores.",
    heroCopy: "Invista em créditos de curto prazo com garantia sénior a promotores imobiliários experientes, financiando a aquisição de terreno, o licenciamento e projetos de pré-construção em Portugal.",
    proof: ["Rentabilidade anual alvo de 8%, paga trimestralmente", "Hipoteca de primeiro grau sobre cada ativo", "A Verde investe o seu próprio capital em cada operação"],
    formTitle: "Quero Investir",
    done: "Pedido recebido.",
    doneCopy: "A Verde Capital entrará em contacto em breve. Foi enviada uma confirmação para ",
    name: "Nome *", email: "Email *", country: "País *", itype: "Tipo de investidor *",
    itypePh: "Selecione", ticket: "Ticket indicativo *", ticketPh: "Selecione",
    message: "Mensagem",
    consent: "Autorizo a Verde Capital a contactar-me sobre oportunidades de investimento.",
    err: "Algo falhou. Tente novamente.",
    sending: "A enviar…", submit: "Submeter a minha candidatura",
    investorTypes: ["Family office", "Investidor particular", "Empresário a investir através de empresa", "Consultor", "Outro"],
    tickets: ["€100–250 mil", "€250–500 mil", "€500 mil–1M", "€1M+", "ainda não determinado"],
    secEyebrow: "O que recebe",
    secH: "Os factos, antes de perguntar.",
    securities: [
      { icon: Percent, t: "Juro anual alvo de 8%", d: "Pago trimestralmente, diretamente para a conta bancária por si indicada, durante a vigência do crédito." },
      { icon: CalendarDays, t: "Prazos de 6 – 24 meses", d: "Prazos curtos com uma via de reembolso definida: refinanciamento bancário, venda ou pré-vendas." },
      { icon: ShieldCheck, t: "Hipoteca de primeiro grau", d: "O seu capital é garantido por uma hipoteca de primeiro grau sobre o imóvel subjacente." },
      { icon: PiggyBank, t: "A Verde investe consigo", d: "A Verde Capital investe o seu próprio capital em cada operação, juntamente com o seu." },
      { icon: Layers, t: "Subscrição disciplinada", d: "Avaliações independentes, conhecimento local do mercado e vias de saída claras em cada processo." },
      { icon: MapPin, t: "Foco residencial em Portugal", d: "Créditos sobre projetos residenciais em todo o Portugal — ticket mínimo de €100.000." },
    ],
    oppEyebrow: "A oportunidade",
    oppH1: "Portugal precisa de mais habitação.", oppH2: "Os promotores precisam de capital.",
    oppCopy: "Portugal enfrenta uma escassez significativa de habitação. Procura forte, oferta nova limitada e a complexidade do licenciamento impedem que muitos projetos viáveis cheguem à construção. Fornecemos o capital para as fases iniciais críticas, em que promotores experientes precisam de financiamento antes da entrada dos bancos.",
    oppNumber: "~300.000", oppNumberLabel: "habitações em falta em Portugal",
    oppCard: "Falta estimada de construção de habitação face ao crescimento dos agregados familiares na última década.",
    oppSource: "Fonte: Banco de Portugal, relatório de 2026 sobre o défice de construção de habitação.",
    faqEyebrow: "Perguntas frequentes", faqH: "Perguntas-chave, respostas claras.",
    faqs: [
      { q: "Como é pago o rendimento de 8%?", a: "Trimestralmente, diretamente para a conta bancária por si indicada, durante a vigência do crédito." },
      { q: "Quando recebo o meu capital de volta?", a: "No final do prazo do crédito, reembolsado pela via acordada: refinanciamento bancário, venda ou pré-vendas." },
      { q: "O que garante o meu capital?", a: "Uma hipoteca de primeiro grau sobre o imóvel subjacente — e a Verde Capital investe o seu próprio capital juntamente com o seu em cada operação." },
      { q: "O que é uma hipoteca de primeiro grau?", a: "Significa que a garantia do credor sobre o imóvel tem prioridade sobre a de qualquer outro credor. Se um crédito precisar de ser recuperado através do imóvel, a posição de primeiro grau é reembolsada antes dos credores subordinados. Os créditos da Verde Capital são garantidos desta forma." },
      { q: "Quais são os prazos típicos do crédito?", a: "6 – 24 meses, com uma via de reembolso definida: refinanciamento bancário, venda ou pré-vendas." },
      { q: "Quem pode investir?", a: "Investidores profissionais e qualificados — particulares, family offices e empresas — a partir de €100.000, sujeito a onboarding e verificação de adequação." },
      { q: "Que comissões se aplicam?", a: "As comissões estão definidas nas condições de cada operação antes de qualquer compromisso. Não há qualquer custo por pedir informação ou falar com a equipa." },
      { q: "Quais são os principais riscos?", a: "O crédito ao desenvolvimento implica riscos reais: atrasos, licenciamento, oscilações de mercado. O capital está em risco e desempenhos passados não garantem retornos futuros. Os nossos mitigantes são posições com garantia sénior, hipotecas de primeiro grau, rácios empréstimo-valor baixos e prazos curtos." },
    ],
    contactEyebrow: "Conversa com investidores",
    contactH1: "Vamos falar do", contactH2: "seu investimento.",
    contactCopy: "Agende uma chamada com a nossa equipa de relações com investidores para falar da estratégia, do pipeline atual e das condições.",
    team: "Relações com investidores da Verde Capital", line: "Uma linha direta. Sem call centres.",
    book: "Agendar reunião", callPre: "Ou ligue: ",
    alt: "Estaleiro de desenvolvimento costeiro em Portugal ao anoitecer",
    disclaimer: "O capital está em risco e desempenhos passados não garantem retornos futuros. Todo o crédito está sujeito a subscrição, garantias e due diligence completa.",
  },
  nl: {
    eyebrow: "Investeerdersinformatie",
    h1a: "Informatie aanvragen",
    h1b: "voor investeerders.",
    heroCopy: "Beleg in kortlopende, senior gedekte leningen aan ervaren vastgoedontwikkelaars die grondverwerving, vergunningen en voorbouwfase-projecten in Portugal financieren.",
    proof: ["8% streefrendement per jaar, per kwartaal uitgekeerd", "Eerste rangs hypotheek op elk actief", "Verde belegt eigen kapitaal in elke transactie"],
    formTitle: "Ik wil investeren",
    done: "Aanvraag ontvangen.",
    doneCopy: "Verde Capital neemt zo snel mogelijk contact op. Een bevestiging is verzonden naar ",
    name: "Naam *", email: "E-mail *", country: "Land *", itype: "Type belegger *",
    itypePh: "Kies", ticket: "Indicatief ticket *", ticketPh: "Kies",
    message: "Bericht",
    consent: "Ik geef Verde Capital toestemming om mij te benaderen over beleggingskansen.",
    err: "Er ging iets mis. Probeer het opnieuw.",
    sending: "Versturen…", submit: "Mijn aanvraag indienen",
    investorTypes: ["Family office", "Particuliere belegger", "Ondernemer die via een vennootschap belegt", "Adviseur", "Anders"],
    tickets: ["€100–250k", "€250–500k", "€500k–1 mln", "€1 mln+", "nog niet bepaald"],
    secEyebrow: "Wat u krijgt",
    secH: "De feiten, vóórdat u vraagt.",
    securities: [
      { icon: Percent, t: "8% streefrente per jaar", d: "Per kwartaal uitgekeerd, rechtstreeks naar de bankrekening die u opgeeft, gedurende de looptijd." },
      { icon: CalendarDays, t: "Looptijden van 6 – 24 maanden", d: "Korte looptijden met een duidelijke aflossingsroute: bankherfinanciering, verkoop of voorverkoop." },
      { icon: ShieldCheck, t: "Eerste rangs hypotheek", d: "Uw kapitaal is gedekt door een eerste rangs hypotheek op de onderliggende woning." },
      { icon: PiggyBank, t: "Verde belegt mee", d: "Verde Capital belegt eigen kapitaal in elke transactie, naast dat van u." },
      { icon: Layers, t: "Gedisciplineerde beoordeling", d: "Onafhankelijke taxaties, lokale marktkennis en heldere exits in elk dossier." },
      { icon: MapPin, t: "Residentiële focus in Portugal", d: "Leningen op woningbouwprojecten in heel Portugal — minimaal ticket van €100.000." },
    ],
    oppEyebrow: "De kans",
    oppH1: "Portugal heeft meer woningen nodig.", oppH2: "Ontwikkelaars hebben kapitaal nodig.",
    oppCopy: "Portugal kent een groot woningtekort. Sterke vraag, beperkt nieuw aanbod en planologische complexiteit houden veel levensvatbare projecten van de bouwfase af. Wij financieren de kritieke vroege fasen waarin ervaren ontwikkelaars kapitaal nodig hebben vóórdat banken instappen.",
    oppNumber: "~300.000", oppNumberLabel: "woningen tekort in Portugal",
    oppCard: "Geschat bouwtekort voor woningen ten opzichte van de huishoudensgroei in het afgelopen decennium.",
    oppSource: "Bron: Banco de Portugal, rapportage 2026 over het woningtekort.",
    faqEyebrow: "Veelgestelde vragen", faqH: "Kernvragen, heldere antwoorden.",
    faqs: [
      { q: "Hoe wordt het rendement van 8% uitbetaald?", a: "Per kwartaal, rechtstreeks naar de bankrekening die u opgeeft, gedurende de looptijd van de lening." },
      { q: "Wanneer krijg ik mijn kapitaal terug?", a: "Aan het einde van de looptijd, afgelost via de overeengekomen route: bankherfinanciering, verkoop of voorverkoop." },
      { q: "Waarop is mijn kapitaal gedekt?", a: "Een eerste rangs hypotheek op de onderliggende woning — en Verde Capital belegt eigen kapitaal mee in elke transactie." },
      { q: "Wat is een eerste rangs hypotheek?", a: "Dat de zekerheid van de geldverstrekker op het vastgoed voorrang heeft op die van elke andere schuldeiser. Moet een lening ooit via het vastgoed worden verhaald, dan wordt de eerste rangs positie vóór achtergestelde schuldeisers betaald. De leningen van Verde Capital zijn zo gedekt." },
      { q: "Wat zijn de typische looptijden?", a: "6 – 24 maanden, met een duidelijke aflossingsroute: bankherfinanciering, verkoop of voorverkoop." },
      { q: "Wie kan er investeren?", a: "Professionele en gekwalificeerde beleggers — particulieren, family offices en bedrijven — vanaf €100.000, onder voorbehoud van onboarding en geschiktheidstoets." },
      { q: "Welke kosten gelden er?", a: "De kosten staan in de voorwaarden van elke transactie vóór enige verbintenis. Informatie aanvragen of met het team praten is gratis." },
      { q: "Wat zijn de grootste risico's?", a: "Ontwikkelingskrediet kent reële risico's: vertraging, vergunningen en marktbewegingen. Uw kapitaal staat op het spel en prestaties uit het verleden garanderen niets voor de toekomst. Onze beheersmaatregelen: senior gedekte posities, eerste rangs hypotheken, lage loan-to-value en korte looptijden." },
    ],
    contactEyebrow: "Gesprek met investeerders",
    contactH1: "Laten we uw", contactH2: "investering bespreken.",
    contactCopy: "Plan een gesprek met ons investorrelatieteam over de strategie, de huidige pijplijn en de voorwaarden.",
    team: "Investorrelaties van Verde Capital", line: "Eén directe lijn. Geen callcentres.",
    book: "Gesprek plannen", callPre: "Of bel: ",
    alt: "Kustontwikkelingslocatie in Portugal in de avond",
    disclaimer: "Uw kapitaal staat op het spel en prestaties uit het verleden garanderen niets voor de toekomst. Alle kredietverlening is onder voorbehoud van kredietbeoordeling, zekerheden en volledige due diligence.",
  },
};

export default function InvestorInfo() {
  const { lang, lp } = useLang();
  const c = COPY[lang];
  const [form, setForm] = useState({ name: "", email: "", country: "", investor_type: "", ticket: "", message: "", consent: false });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const valid = form.name.trim() && /.+@.+\..+/.test(form.email) && form.country.trim() && form.investor_type && form.ticket && form.consent;

  const submit = async (e) => {
    e.preventDefault();
    if (!valid || submitting) return;
    setSubmitting(true);
    setError("");
    try {
      await post("/investor-requests", { ...form, lang, ...leadSourceFields() });
      measureLead();
      setDone(true);
      window.scrollTo(0, 0);
    } catch {
      setError(c.err);
    }
    setSubmitting(false);
  };

  return (
    <div className="bg-cream">
      <JsonLd data={{
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: c.faqs.map((f) => ({
          "@type": "Question", name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      }} />
      {/* ── HERO + REQUEST FORM ──────────────────────────── */}
      <section className="relative overflow-hidden bg-verde-950" data-testid="investor-info-hero">
        <img
          src={HERO_IMG}
          alt={c.alt}
          className="absolute inset-0 h-full w-full object-cover opacity-40"
          data-testid="investor-info-hero-photo"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-verde-950 via-verde-950/85 to-verde-950/50" />
        <div className="container-x relative pt-28 sm:pt-36 pb-16 sm:pb-20 lg:pb-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:items-center">
            <div className="lg:col-span-7">
              <Eyebrow light>{c.eyebrow}</Eyebrow>
              <h1 className="display mt-6 text-[2.6rem] sm:text-5xl lg:text-[56px]" data-testid="page-headline">
                <MaskLineView>
                  <span className="text-mint">{c.h1a}</span>
                </MaskLineView>
                <MaskLineView delay={0.12}>
                  <span className="text-white">{c.h1b}</span>
                </MaskLineView>
              </h1>
              <Reveal delay={0.25} className="mt-7 max-w-xl">
                <p className="text-lg leading-relaxed text-cream/85">{c.heroCopy}</p>
              </Reveal>
              <Reveal delay={0.35}>
                <ul className="mt-8 space-y-3" data-testid="investor-info-proof">
                  {c.proof.map((p) => (
                    <li key={p} className="flex items-start gap-3 text-sm text-cream/85">
                      <span className="mt-1 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-mint">
                        <Check className="h-2.5 w-2.5 text-verde-950" strokeWidth={3.5} />
                      </span>
                      {p}
                    </li>
                  ))}
                </ul>
              </Reveal>
            </div>

            <div className="lg:col-span-5" data-testid="investor-info-form-card">
              <Reveal delay={0.2}>
                <div className="rounded-2xl bg-white p-6 sm:p-8 shadow-2xl shadow-verde-950/30">
                  {done ? (
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="py-8 text-center" data-testid="investor-info-confirmation">
                      <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-verde-800">
                        <Check className="h-5 w-5 text-mint" />
                      </span>
                      <p className="mt-6 font-display text-2xl font-bold text-charcoal">{c.done}</p>
                      <p className="mt-3 text-sm leading-relaxed text-ink">
                        {c.doneCopy}
                        <span className="font-semibold">{form.email}</span>.
                      </p>
                    </motion.div>
                  ) : (
                    <form onSubmit={submit} className="space-y-4" data-testid="investor-info-form">
                      <p className="font-display text-xl font-bold text-charcoal">{c.formTitle}</p>
                      <div>
                        <label className={label}>{c.name}</label>
                        <input className={field} value={form.name} onChange={set("name")} data-testid="investor-info-name" />
                      </div>
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                          <label className={label}>{c.email}</label>
                          <input type="email" className={field} value={form.email} onChange={set("email")} data-testid="investor-info-email" />
                        </div>
                        <div>
                          <label className={label}>{c.country}</label>
                          <input className={field} value={form.country} onChange={set("country")} data-testid="investor-info-country" />
                        </div>
                      </div>
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                          <label className={label}>{c.itype}</label>
                          <select className={field} value={form.investor_type} onChange={set("investor_type")} data-testid="investor-info-type">
                            <option value="" disabled>{c.itypePh}</option>
                            {c.investorTypes.map((v) => <option key={v}>{v}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className={label}>{c.ticket}</label>
                          <select className={field} value={form.ticket} onChange={set("ticket")} data-testid="investor-info-ticket">
                            <option value="" disabled>{c.ticketPh}</option>
                            {c.tickets.map((v) => <option key={v}>{v}</option>)}
                          </select>
                        </div>
                      </div>
                      <div>
                        <label className={label}>{c.message}</label>
                        <textarea rows={3} className={field} value={form.message} onChange={set("message")} data-testid="investor-info-message" />
                      </div>
                      <label className="flex items-start gap-2.5 text-xs text-ink">
                        <input type="checkbox" checked={form.consent} onChange={(e) => setForm((f) => ({ ...f, consent: e.target.checked }))} data-testid="investor-info-consent" className="mt-0.5 h-4 w-4 shrink-0 rounded border-sand accent-verde-800" />
                        {c.consent}
                      </label>
                      {error && <p className="text-sm text-red-700" data-testid="investor-info-error">{error}</p>}
                      <button
                        type="submit"
                        disabled={!valid || submitting}
                        className="group inline-flex w-full items-center justify-center gap-2.5 rounded-full bg-verde-800 px-7 py-3.5 text-sm font-semibold text-white transition-all hover:bg-verde-700 disabled:cursor-not-allowed disabled:opacity-40"
                        data-testid="investor-info-submit"
                      >
                        {submitting ? c.sending : c.submit}
                        {!submitting && <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />}
                      </button>
                    </form>
                  )}
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* ── WHAT YOU GET ─────────────────────────────────── */}
      <section className="py-20 lg:py-28" data-testid="investor-info-securities">
        <div className="container-x">
          <Eyebrow>{c.secEyebrow}</Eyebrow>
          <h2 className="display mt-5 text-3xl sm:text-4xl text-charcoal max-w-2xl">
            <MaskLineView>{c.secH}</MaskLineView>
          </h2>
          <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {c.securities.map((s, i) => (
              <Reveal key={s.t} delay={0.06 + i * 0.06}>
                <div className="card h-full p-7" data-testid={`investor-info-security-${i}`}>
                  <span className="relative flex h-11 w-11 items-center justify-center overflow-hidden rounded-2xl bg-gradient-to-br from-[#EAF3EE] via-[#E0EEE6] to-[#CFE5D9] ring-1 ring-verde-800/10 shadow-[0_12px_26px_-16px_rgba(7,39,29,0.45)]">
                    <span className="absolute inset-0 bg-gradient-to-b from-white/70 via-transparent to-transparent" aria-hidden="true" />
                    <s.icon className="relative h-5 w-5 text-verde-800" strokeWidth={1.7} aria-hidden="true" />
                  </span>
                  <h3 className="mt-5 font-display text-lg font-bold text-charcoal">{s.t}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-ink">{s.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── THE OPPORTUNITY ──────────────────────────────── */}
      <section className="py-20 lg:py-28 bg-white border-y border-sand" data-testid="investor-info-opportunity">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <Eyebrow>{c.oppEyebrow}</Eyebrow>
            <h2 className="display mt-5 text-3xl sm:text-4xl text-charcoal">
              <MaskLineView>{c.oppH1}</MaskLineView>
              <MaskLineView delay={0.1}>{c.oppH2}</MaskLineView>
            </h2>
            <Reveal delay={0.2} className="mt-6 max-w-xl">
              <p className="text-[15px] leading-relaxed text-fog">{c.oppCopy}</p>
            </Reveal>
          </div>
          <Reveal delay={0.15} className="lg:col-span-5">
            <div className="rounded-2xl bg-verde-950 p-8 text-cream grain" data-testid="investor-info-stat">
              <p className="font-display text-5xl font-black text-mint">{c.oppNumber}</p>
              <p className="mt-2 text-sm font-semibold text-white">{c.oppNumberLabel}</p>
              <p className="mt-4 text-xs leading-relaxed text-cream/70">{c.oppCard}</p>
              <p className="mt-3 text-[11px] text-cream/45">{c.oppSource}</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── FAQ ──────────────────────────────────────────── */}
      <section className="py-20 lg:py-28" data-testid="investor-info-faq-section">
        <div className="container-x max-w-4xl">
          <Eyebrow>{c.faqEyebrow}</Eyebrow>
          <h2 className="display mt-5 text-3xl sm:text-4xl text-charcoal">
            <MaskLineView>{c.faqH}</MaskLineView>
          </h2>
          <Reveal delay={0.15} className="mt-10">
            <Faq items={c.faqs} />
          </Reveal>
        </div>
      </section>

      {/* ── CONTACT ──────────────────────────────────────── */}
      <section className="py-20 lg:py-28 bg-verde-950 text-cream grain relative" data-testid="investor-info-contact">
        <div className="container-x relative grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <Eyebrow light>{c.contactEyebrow}</Eyebrow>
            <h2 className="display mt-5 text-3xl sm:text-4xl lg:text-[44px] text-white">
              <MaskLineView>{c.contactH1}</MaskLineView>
              <MaskLineView delay={0.1}>{c.contactH2}</MaskLineView>
            </h2>
            <Reveal delay={0.2} className="mt-5 max-w-lg">
              <p className="text-[15px] leading-relaxed text-cream/75">{c.contactCopy}</p>
            </Reveal>
            <Reveal delay={0.25} className="mt-6 flex items-center gap-4">
              <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full border border-brass/60 bg-white/5 font-display text-lg font-black text-brass">
                VC
              </span>
              <div>
                <p className="text-sm font-semibold text-white">{c.team}</p>
                <p className="text-xs text-cream/60">{c.line}</p>
              </div>
            </Reveal>
            <Reveal delay={0.3} className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center">
              <BookMeeting label={c.book} variant="primary" testid="investor-info-book" />
              <a
                href={PHONE_TEL}
                data-testid="investor-info-call"
                className="inline-flex items-center justify-center gap-2.5 rounded-full border border-white/25 px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-white/10"
              >
                <Phone className="h-4 w-4" />
                {c.callPre}{PHONE_DISPLAY}
              </a>
            </Reveal>
          </div>
          <Reveal delay={0.2} className="lg:col-span-5">
            <figure className="overflow-hidden rounded-2xl border border-white/10" data-testid="investor-info-figure">
              <img loading="lazy" decoding="async" src="/investors-conversation.jpg" alt={c.alt} className="h-72 w-full object-cover lg:h-96" />
            </figure>
          </Reveal>
        </div>
      </section>

      <section className="py-10">
        <div className="container-x max-w-3xl">
          <p className="border-t border-sand pt-6 text-xs leading-relaxed text-fog" data-testid="investor-info-disclaimer">
            {c.disclaimer}
          </p>
        </div>
      </section>
    </div>
  );
}
